import type { ITeam } from 'state/knovStore'
import type { Quest } from 'types/quests'

type SeedingStream = Partial<ITeam>
type QuestWithStream = Pick<Quest, 'team' | 'team_id'>
type SignalQuestSnapshot = Pick<Quest, 'id' | 'team' | 'team_id'>

export type SeedingMode = 'upvalue_only' | 'user_choice' | 'force_seed'

export function resolveSignalQuestSnapshot(
    questId: string | number | null | undefined,
    renderedSignalQuest: SignalQuestSnapshot | null | undefined,
    cachedSignalQuest: QuestWithStream | null | undefined,
): QuestWithStream | null {
    if (
        questId != null &&
        renderedSignalQuest &&
        String(renderedSignalQuest.id) === String(questId)
    ) {
        return renderedSignalQuest
    }

    return cachedSignalQuest || null
}

export function seedingControlsForMode(
    mode: SeedingMode,
    { featureEnabled }: { featureEnabled: boolean },
) {
    return {
        canChooseSeed: featureEnabled && mode === 'user_choice',
        forceSeed: featureEnabled && mode === 'force_seed',
        requestedSeed: (selectedSeed: boolean) =>
            featureEnabled && (mode === 'force_seed' || (mode === 'user_choice' && selectedSeed)),
    }
}

export function resolveSeedingStream(
    quest: QuestWithStream | null | undefined,
    currentStreams: readonly SeedingStream[],
): SeedingStream | null {
    const embeddedTeam = quest?.team as SeedingStream | null | undefined
    const teamId = quest?.team_id || embeddedTeam?.id
    const currentTeam = teamId
        ? currentStreams.find(team => String(team.id) === String(teamId))
        : null

    return currentTeam || embeddedTeam || null
}

export function updateStreamSeedSelection<T extends SeedingStream>(
    streams: readonly T[],
    streamId: string,
    seedSelected: boolean,
): T[] {
    return streams.map(stream =>
        String(stream.id) === String(streamId)
            ? ({ ...stream, seed_selected: seedSelected } as T)
            : stream,
    )
}
